// src/components/RegionImageCropper.js - REFACTORED FOR BASE64

import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Typography,
    Grid,
    Button,
    Slider,
    Card,
    CardContent,
    IconButton,
    Alert
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

import { DEFAULT_CROP } from './RegionConstants';
import { generateImageFilename } from './RegionUtils';
import { fileToBase64 } from '../utils/fileUtils';

/**
 * RegionImageCropper Component
 * 
 * Handles PDF image display, cropping, and image file generation
 * for region bone length and width measurements.
 * 
 * @param {Object} props - Component props
 * @param {string|number} props.regionNumber - Region number or identifier
 * @param {Array<string>} props.convertedImages - Array of base64 image strings from PDF
 * @param {number} props.currentImageIndex - Current selected image index
 * @param {Object} props.crop - Current crop configuration
 * @param {Object} props.completedCrop - Completed crop configuration
 * @param {string|null} props.croppedImageUrl1 - Base64 string for first cropped image
 * @param {string|null} props.croppedImageUrl2 - Base64 string for second cropped image
 * @param {Object|null} props.croppedImageFile1 - Base64 data object for first cropped image
 * @param {Object|null} props.croppedImageFile2 - Base64 data object for second cropped image
 * @param {Function} props.onImageIndexChange - Callback for image index changes
 * @param {Function} props.onCropChange - Callback for crop changes
 * @param {Function} props.onCropComplete - Callback for crop completion
 * @param {Function} props.onCroppedImageChange - Callback for cropped image changes
 * @param {boolean} props.disabled - Whether the component is disabled
 */
const RegionImageCropper = ({
    regionNumber,
    convertedImages = [],
    currentImageIndex = 0,
    crop = DEFAULT_CROP,
    completedCrop = null,
    croppedImageUrl1 = null,
    croppedImageUrl2 = null,
    croppedImageFile1 = null,
    croppedImageFile2 = null,
    onImageIndexChange,
    onCropChange,
    onCropComplete,
    onCroppedImageChange,
    disabled = false
}) => {
    // Refs for image and canvas manipulation
    const [imageRef, setImageRef] = useState(null);
    const canvasRef = useRef(null);

    // Local state for error handling
    const [error, setError] = useState(null);
    
    // Local crop state for smooth cropping
    const [localCrop, setLocalCrop] = useState(crop);
    const [localCompletedCrop, setLocalCompletedCrop] = useState(completedCrop);

    // Sync local crop with props when image changes
    useEffect(() => {
        setLocalCrop(crop);
        setLocalCompletedCrop(completedCrop);
    }, [currentImageIndex]);


    // Note: No cleanup needed with Base64 approach - images are stored as strings

    // Reset error when image changes
    useEffect(() => {
        setError(null);
    }, [currentImageIndex]);

    /**
     * Handle slider change for image navigation
     * @param {Event} event - Slider change event
     * @param {number} newValue - New slider value
     */
    const handleSliderChange = useCallback((event, newValue) => {
        if (onImageIndexChange) {
            onImageIndexChange(newValue);
        }
        setError(null);
    }, [onImageIndexChange]);

    /**
     * Handle image load event
     * @param {Event} e - Image load event
     */
    const onImageLoad = useCallback((e) => {
        const image = e.target;
        setImageRef(image);
        setError(null);
        console.log(`RegionImageCropper ${regionNumber}: Image loaded`, {
            naturalWidth: image.naturalWidth,
            naturalHeight: image.naturalHeight,
            displayWidth: image.width,
            displayHeight: image.height
        });
    }, [regionNumber]);

    /**
     * Get cropped image from canvas
     * @param {HTMLImageElement} image - Source image element
     * @param {Object} cropData - Crop configuration
     * @returns {Promise<Blob|null>} Promise resolving to cropped image blob
     */
    const getCroppedImage = useCallback((image, cropData) => {
        const canvas = canvasRef.current;
        
        if (!canvas || !image) {
            console.error(`RegionImageCropper ${regionNumber}: Canvas or image not available`);
            return Promise.resolve(null);
        }

        if (!cropData || cropData.width <= 0 || cropData.height <= 0) {
            console.error(`RegionImageCropper ${regionNumber}: Invalid crop data`, cropData);
            return Promise.resolve(null);
        }

        try {
            const scaleX = image.naturalWidth / image.width;
            const scaleY = image.naturalHeight / image.height;
            
            canvas.width = cropData.width * scaleX;
            canvas.height = cropData.height * scaleY;
            
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                console.error(`RegionImageCropper ${regionNumber}: Could not get canvas context`);
                return Promise.resolve(null);
            }

            const sourceX = cropData.x * scaleX;
            const sourceY = cropData.y * scaleY;
            const sourceWidth = cropData.width * scaleX;
            const sourceHeight = cropData.height * scaleY;

            console.log(`RegionImageCropper ${regionNumber}: Drawing cropped image`, {
                sourceX, sourceY, sourceWidth, sourceHeight,
                destWidth: canvas.width, destHeight: canvas.height
            });

            ctx.drawImage(
                image,
                sourceX, sourceY, sourceWidth, sourceHeight,
                0, 0, canvas.width, canvas.height
            );

            return new Promise((resolve) => {
                canvas.toBlob((blob) => {
                    if (blob) {
                        console.log(`RegionImageCropper ${regionNumber}: Blob created, size:`, blob.size);
                    } else {
                        console.error(`RegionImageCropper ${regionNumber}: Failed to create blob`);
                    }
                    resolve(blob);
                }, 'image/png', 1);
            });
        } catch (error) {
            console.error(`RegionImageCropper ${regionNumber}: Error in getCroppedImage:`, error);
            return Promise.resolve(null);
        }
    }, [regionNumber]);

    /**
     * Handle crop image action
     * @param {number} imageNumber - Image number (1 or 2)
     */
    const handleCropImage = useCallback(async (imageNumber) => {
        const cropToUse = localCompletedCrop || localCrop;
        
        if (!imageRef) {
            setError('No image loaded for cropping');
            return;
        }

        if (!cropToUse || cropToUse.width <= 0 || cropToUse.height <= 0) {
            setError('Please select an area to crop');
            return;
        }

        try {
            setError(null);

            const croppedBlob = await getCroppedImage(imageRef, cropToUse);
            
            if (!croppedBlob) {
                setError('Failed to create cropped image');
                return;
            }

            // Convert blob to Base64
            const base64 = await fileToBase64(croppedBlob);
            const filename = generateImageFilename(regionNumber, imageNumber);
            
            // Create the Base64 image data object
            const imageData = {
                base64,
                name: filename,
                type: 'image/png'
            };

            if (onCroppedImageChange) {
                onCroppedImageChange(imageNumber, imageData);
            }
        } catch (error) {
            console.error(`RegionImageCropper ${regionNumber}: Crop failed:`, error);
            setError('Failed to crop image. Please try again.');
        }
    }, [regionNumber, imageRef, localCrop, localCompletedCrop, getCroppedImage, onCroppedImageChange]);

    /**
     * Handle remove cropped image action
     * @param {number} imageNumber - Image number (1 or 2)
     */
    const handleRemoveCroppedImage = useCallback((imageNumber) => {
        if (onCroppedImageChange) {
            onCroppedImageChange(imageNumber, null);
        }
    }, [onCroppedImageChange]);

    /**
     * Handle crop change
     * @param {Object} c - New crop configuration
     */
    const handleCropChange = useCallback((c) => {
        setLocalCrop(c);
        // Debounce parent updates
        if (onCropChange) {
            onCropChange(c);
        }
    }, [onCropChange]);

    /**
     * Handle crop complete
     * @param {Object} c - Completed crop configuration
     */
    const handleCropComplete = useCallback((c) => {
        setLocalCompletedCrop(c);
        if (onCropComplete) {
            onCropComplete(c);
        }
    }, [onCropComplete]);

    /**
     * Render cropped image preview
     * @param {number} imageNumber - Image number (1 or 2)
     * @param {string} title - Image title
     * @param {string|null} imageUrl - Image URL
     * @param {File|null} imageFile - Image file
     * @returns {JSX.Element} Image preview component
     */
    const renderImagePreview = (imageNumber, title, imageUrl, imageFile) => (
        <Grid item xs={12} md={6} key={imageNumber}>
            <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="subtitle1">{title}</Typography>
                        {imageFile && (
                            <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveCroppedImage(imageNumber)}
                                title="Remove image"
                                disabled={disabled}
                            >
                                <DeleteIcon />
                            </IconButton>
                        )}
                    </Box>

                    {imageUrl ? (
                        <Box sx={{
                            border: '1px solid #ddd',
                            borderRadius: 1,
                            overflow: 'hidden',
                            backgroundColor: '#f5f5f5',
                            mb: 2
                        }}>
                            <img
                                src={imageUrl}
                                alt={title}
                                style={{
                                    width: '100%',
                                    height: '200px',
                                    objectFit: 'contain',
                                    display: 'block'
                                }}
                            />
                        </Box>
                    ) : (
                        <Box sx={{
                            height: '200px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '2px dashed #ccc',
                            borderRadius: 1,
                            mb: 2
                        }}>
                            <Typography variant="body2" color="textSecondary">
                                No image set yet
                            </Typography>
                        </Box>
                    )}

                    {imageFile && (
                        <Typography variant="caption" color="success.main">
                            {imageFile.name}
                        </Typography>
                    )}
                </CardContent>
            </Card>
        </Grid>
    );

    // Don't render if no images available
    if (!convertedImages || convertedImages.length === 0) {
        return (
            <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="textSecondary">
                    No PDF images available for cropping. Please upload a PDF file first.
                </Typography>
            </Box>
        );
    }

    return (
        <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    Image Selection & Cropping for Region {regionNumber}
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {/* Image Navigation */}
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" gutterBottom>
                        Page {currentImageIndex + 1} of {convertedImages.length}
                    </Typography>
                    <Slider
                        value={currentImageIndex}
                        onChange={handleSliderChange}
                        min={0}
                        max={convertedImages.length - 1}
                        step={1}
                        marks
                        valueLabelDisplay="auto"
                        disabled={disabled}
                    />
                </Box>

                <Grid container spacing={2}>
                    {/* Image Cropping Area */}
                    <Grid item xs={12} md={8}>
                        <Box position="relative">
                            <ReactCrop
                                crop={localCrop}
                                onChange={handleCropChange}
                                onComplete={handleCropComplete}
                                disabled={disabled}
                                key={`${regionNumber}-${currentImageIndex}`}
                            >
                                <img
                                    src={`data:image/png;base64,${convertedImages[currentImageIndex]}`}
                                    alt={`Page ${currentImageIndex + 1}`}
                                    style={{ maxWidth: '100%', height: 'auto' }}
                                    onLoad={onImageLoad}
                                    onError={() => setError('Failed to load image')}
                                />
                            </ReactCrop>
                            {localCompletedCrop && imageRef && (
                                <Box style={{ position: 'absolute', top: `${localCompletedCrop.y + localCompletedCrop.height + 5}px`, left: `${localCompletedCrop.x}px`, display: 'flex', gap: '5px' }}>
                                    <Button variant="contained" size="small" onClick={() => handleCropImage(1)}>Length</Button>
                                    <Button variant="contained" size="small" onClick={() => handleCropImage(2)}>Width</Button>
                                </Box>
                            )}
                        </Box>
                    </Grid>

                    
                </Grid>

                {/* Cropped Images Preview */}
                <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                        Cropped Images Preview
                    </Typography>
                    <Grid container spacing={2}>
                        {renderImagePreview(1, "Bone Length Image", croppedImageUrl1, croppedImageFile1)}
                        {renderImagePreview(2, "Bone Width Image", croppedImageUrl2, croppedImageFile2)}
                    </Grid>
                </Box>

                {/* Summary */}
                <Box sx={{ mt: 2, p: 1, bgcolor: 'info.light', borderRadius: 1 }}>
                    <Typography variant="caption">
                        <strong>Status:</strong> {[croppedImageFile1, croppedImageFile2].filter(Boolean).length} of 2 images cropped
                        {convertedImages.length > 0 && ` • ${convertedImages.length} PDF pages available`}
                    </Typography>
                </Box>

                {/* Hidden canvas for image processing */}
                <canvas ref={canvasRef} style={{ display: 'none' }} />
            </CardContent>
        </Card>
    );
};

RegionImageCropper.propTypes = {
    regionNumber: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]).isRequired,
    convertedImages: PropTypes.arrayOf(PropTypes.string),
    currentImageIndex: PropTypes.number,
    crop: PropTypes.shape({
        unit: PropTypes.string,
        width: PropTypes.number,
        height: PropTypes.number,
        x: PropTypes.number,
        y: PropTypes.number
    }),
    completedCrop: PropTypes.shape({
        unit: PropTypes.string,
        width: PropTypes.number,
        height: PropTypes.number,
        x: PropTypes.number,
        y: PropTypes.number
    }),
    croppedImageUrl1: PropTypes.string,
    croppedImageUrl2: PropTypes.string,
    croppedImageFile1: PropTypes.object,
    croppedImageFile2: PropTypes.object,
    onImageIndexChange: PropTypes.func,
    onCropChange: PropTypes.func,
    onCropComplete: PropTypes.func,
    onCroppedImageChange: PropTypes.func,
    disabled: PropTypes.bool
};

export default RegionImageCropper;